import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarDays, Clock, ChevronRight, CheckCircle2, ArrowLeft, IndianRupee } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { BOOK } from "../constants/testIds";
import { toast } from "sonner";
import { OtpModal, loadRazorpay } from "../components/OtpModal";
import { UpiQrPayModal } from "../components/UpiQrPayModal";

const SERVICE_META = {
  ultrasound: { needsTest: true,  needsDoctor: false, needsHospital: true,  needsDept: false, needsRoom: false },
  opd:        { needsTest: false, needsDoctor: false, needsHospital: true,  needsDept: false, needsRoom: false },
  ipd:        { needsTest: false, needsDoctor: true,  needsHospital: true,  needsDept: true,  needsRoom: true  },
  xray:       { needsTest: true,  needsDoctor: false, needsHospital: true,  needsDept: false, needsRoom: false },
  ct:         { needsTest: true,  needsDoctor: false, needsHospital: true,  needsDept: false, needsRoom: false },
  mri:        { needsTest: true,  needsDoctor: false, needsHospital: true,  needsDept: false, needsRoom: false },
};

const ROOM_TYPES = ["General Ward", "Semi-Private", "Private", "Deluxe", "ICU"];

export default function BookingFlow() {
  const { service } = useParams();
  const meta = SERVICE_META[service];
  const { lang, user } = useApp();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [tests, setTests] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [slots, setSlots] = useState([]);
  const [fees, setFees] = useState({});
  const [form, setForm] = useState({
    test_id: "", doctor_id: "", hospital_id: "", department: "", room_type: "",
    slot_date: new Date(Date.now() + 24*3600*1000).toISOString().slice(0,10),
    slot_time: "",
    patient: { name: user?.name || "", age: 30, gender: "male", phone: "", email: user?.email || "", address: "" },
  });
  const [placing, setPlacing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponErr, setCouponErr] = useState("");
  const [publicCoupons, setPublicCoupons] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [locality, setLocality] = useState("");
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpToken, setOtpToken] = useState(null);

  useEffect(() => {
    if (!meta) { navigate("/"); return; }
    (async () => {
      const f = await api.get("/services/fees"); setFees(f.data);
      if (meta.needsTest) { const r = await api.get(`/tests?service_type=${service}`); setTests(r.data); }
      if (meta.needsDoctor) { const r = await api.get("/doctors"); setDoctors(r.data); }
      if (meta.needsHospital) { const r = await api.get("/hospitals"); setHospitals(r.data); }
    })();
  }, [service, meta, navigate]);

  useEffect(() => {
    (async () => {
      if (!form.slot_date) return;
      const r = await api.get(`/slots?date=${form.slot_date}${form.doctor_id ? `&doctor_id=${form.doctor_id}` : ""}`);
      setSlots(r.data);
    })();
  }, [form.slot_date, form.doctor_id]);

  useEffect(() => {
    api.get("/coupons/public").then(r => setPublicCoupons(r.data)).catch(() => {});
    api.get("/localities?city=Begusarai").then(r => setLocalities(r.data.localities || [])).catch(() => {});
  }, []);

  const filteredDoctors = useMemo(() => {
    if (!form.department) return doctors;
    return doctors.filter(d => d.department === form.department);
  }, [doctors, form.department]);

  const selectedTest = tests.find(t => t.test_id === form.test_id);
  const selectedDoctor = doctors.find(d => d.doctor_id === form.doctor_id);
  const platformFee = fees[service] || 99;
  const subtotal = platformFee + (selectedTest?.price || 0) + (service === "opd" && selectedDoctor ? (selectedDoctor.fee || 0) : 0);
  const total = Math.max(0, subtotal - couponDiscount);

  const applyCoupon = async () => {
    setCouponErr("");
    if (!couponCode.trim()) return;
    try {
      const r = await api.post("/coupons/validate", { code: couponCode.trim(), amount: subtotal, service });
      setCouponDiscount(r.data.discount);
      toast.success(`Coupon applied: −₹${r.data.discount}`);
    } catch (e) {
      setCouponDiscount(0);
      setCouponErr(e?.response?.data?.detail || "Invalid coupon");
    }
  };
  const removeCoupon = () => { setCouponCode(""); setCouponDiscount(0); setCouponErr(""); };

  const canProceed = () => {
    if (step === 1) {
      if (meta.needsTest && !form.test_id) return false;
      if (meta.needsHospital && !form.hospital_id) return false;
      if (meta.needsDept && !form.department) return false;
      if (meta.needsDoctor && !form.doctor_id) return false;
      if (meta.needsRoom && !form.room_type) return false;
      if (!form.slot_date || !form.slot_time) return false;
      return true;
    }
    const p = form.patient;
    return p.name && p.age && p.phone;
  };

  const placeBooking = async (tokenOverride) => {
    if (!user) { navigate("/login"); return; }
    const tok = tokenOverride || otpToken;
    if (!tok) { setOtpOpen(true); return; }
    setPlacing(true);
    try {
      const payload = { service_type: service, ...form,
        coupon_code: couponDiscount > 0 ? couponCode.trim() : null,
        otp_token: tok,
      };
      const r = await api.post("/bookings", payload);
      setConfirmed(r.data);
      const o = await api.post("/payments/create-order", { booking_id: r.data.booking_id, type: "booking" });
      setOrderInfo(o.data);
      if (o.data.mock) {
        setPayOpen(true);
      } else {
        await openRazorpay(o.data, r.data.booking_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    }
    setPlacing(false);
  };

  const openRazorpay = async (order, bookingId) => {
    const ok = await loadRazorpay();
    if (!ok) { toast.error("Payment SDK failed to load"); return; }
    const rzp = new window.Razorpay({
      key: order.key_id, amount: order.amount, currency: order.currency,
      order_id: order.order_id, name: order.merchant_name,
      description: `Booking ${bookingId}`,
      prefill: order.prefill,
      theme: { color: "#2563EB" },
      handler: async (resp) => {
        try {
          const v = await api.post("/payments/verify", {
            booking_id: bookingId, type: "booking",
            order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          navigate(`/payment/success?booking_id=${bookingId}&txn=${v.data.transaction_id}`);
        } catch { navigate("/payment/failed"); }
      },
      modal: { ondismiss: () => toast.info("Payment cancelled") },
    });
    rzp.open();
  };

  const completePayment = async () => {
    try {
      const r = await api.post("/payments/verify", {
        booking_id: confirmed.booking_id, type: "booking", order_id: orderInfo.order_id,
      });
      navigate(`/payment/success?booking_id=${confirmed.booking_id}&txn=${r.data.transaction_id}`);
    } catch {
      navigate("/payment/failed");
    }
  };

  const serviceLabel = t(lang, `services.${service}.name`);

  return (
    <div className={lang === "hi" ? "lang-hi" : ""}>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate("/")} className="text-sm text-slate-500 hover:text-blue-600 inline-flex items-center gap-1">
          <ArrowLeft size={16}/> Home
        </button>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-4">{serviceLabel}</h1>
        <p className="text-slate-600 mt-1">{t(lang, `services.${service}.desc`)}</p>

        {/* Steps */}
        <div className="mt-6 flex items-center gap-3 text-sm">
          {[1,2,3].map(s => (
            <div key={s} className={`flex items-center gap-2 ${s <= step ? "text-blue-600" : "text-slate-400"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${s <= step ? "bg-blue-600 text-white" : "bg-slate-100"}`}>{s}</div>
              <span className="hidden sm:inline">{s === 1 ? (lang==="hi"?"चयन":"Select") : s === 2 ? (lang==="hi"?"मरीज़":"Patient") : (lang==="hi"?"भुगतान":"Payment")}</span>
              {s < 3 && <ChevronRight size={14}/>}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          <div className="lg:col-span-2 space-y-5">
            {step === 1 && (
              <div className="bg-white rounded-3xl card-shadow p-6 space-y-5">
                {meta.needsHospital && (
                  <div>
                    <label className="text-sm font-medium">{lang==="hi"?"अस्पताल / सेंटर":"Hospital / Center"}</label>
                    {localities.length > 0 && (
                      <div className="mt-1 mb-2 flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          onClick={() => setLocality("")}
                          data-testid="locality-chip-all"
                          className={`px-3 py-1 text-xs rounded-full border ${!locality ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 hover:border-blue-400"}`}
                        >
                          {lang==="hi"?"सभी इलाके":"All localities"}
                        </button>
                        {localities.slice(0, 8).map(loc => (
                          <button
                            key={loc}
                            type="button"
                            data-testid={`locality-chip-${loc}`}
                            onClick={() => setLocality(loc)}
                            className={`px-3 py-1 text-xs rounded-full border ${locality === loc ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 hover:border-blue-400"}`}
                          >
                            {loc}
                          </button>
                        ))}
                      </div>
                    )}
                    <select data-testid={BOOK.hospitalSelect} value={form.hospital_id}
                      onChange={e => setForm({ ...form, hospital_id: e.target.value })}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none">
                      <option value="">— Select —</option>
                      {hospitals
                        .filter(h => h.services.includes(service))
                        .filter(h => !locality || (h.address || "").toLowerCase().includes(locality.toLowerCase()) || (h.locality || "").toLowerCase().includes(locality.toLowerCase()))
                        .map(h => (
                        <option key={h.hospital_id} value={h.hospital_id}>{h.name} · {h.address}</option>
                      ))}
                    </select>
                  </div>
                )}
                {meta.needsTest && (
                  <div>
                    <label className="text-sm font-medium">{lang==="hi"?"टेस्ट चुनें":"Select Test"}</label>
                    <select data-testid={BOOK.testSelect} value={form.test_id}
                      onChange={e => setForm({ ...form, test_id: e.target.value })}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
                      <option value="">— Select —</option>
                      {tests.map(x => <option key={x.test_id} value={x.test_id}>{x.name} — ₹{x.price}</option>)}
                    </select>
                  </div>
                )}
                {meta.needsDept && (
                  <div>
                    <label className="text-sm font-medium">{lang==="hi"?"विभाग":"Department"}</label>
                    <select data-testid={BOOK.departmentSelect} value={form.department}
                      onChange={e => setForm({ ...form, department: e.target.value, doctor_id: "" })}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
                      <option value="">— Select —</option>
                      {[...new Set(doctors.map(d => d.department))].map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                )}
                {meta.needsDoctor && (
                  <div>
                    <label className="text-sm font-medium">{lang==="hi"?"डॉक्टर":"Doctor"}</label>
                    <select data-testid={BOOK.doctorSelect} value={form.doctor_id}
                      onChange={e => setForm({ ...form, doctor_id: e.target.value })}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
                      <option value="">— Select —</option>
                      {filteredDoctors.map(d => <option key={d.doctor_id} value={d.doctor_id}>{d.name} · {d.department} · ₹{d.fee}</option>)}
                    </select>
                  </div>
                )}
                {meta.needsRoom && (
                  <div>
                    <label className="text-sm font-medium">{lang==="hi"?"कमरा":"Room / Bed"}</label>
                    <select data-testid={BOOK.roomSelect} value={form.room_type}
                      onChange={e => setForm({ ...form, room_type: e.target.value })}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
                      <option value="">— Select —</option>
                      {ROOM_TYPES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium flex items-center gap-2"><CalendarDays size={16}/> {t(lang, "common.selectDate")}</label>
                    <input data-testid={BOOK.dateInput} type="date" value={form.slot_date}
                      min={new Date().toISOString().slice(0,10)}
                      onChange={e => setForm({ ...form, slot_date: e.target.value, slot_time: "" })}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"/>
                  </div>
                  <div>
                    <label className="text-sm font-medium flex items-center gap-2"><Clock size={16}/> {t(lang, "common.selectSlot")}</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {slots.map(s => (
                        <button key={s.time} disabled={!s.available}
                          data-testid={BOOK.slotBtn(s.time)}
                          onClick={() => setForm({ ...form, slot_time: s.time })}
                          className={`px-3 py-2 rounded-full text-sm border ${form.slot_time === s.time ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 hover:border-blue-500"} ${!s.available ? "opacity-40 line-through" : ""}`}
                        >{s.time}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="bg-white rounded-3xl card-shadow p-6 space-y-4">
                <h3 className="font-heading text-xl font-semibold">{t(lang, "common.patientDetails")}</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">{t(lang, "common.name")}</label>
                    <input data-testid={BOOK.patientName} value={form.patient.name}
                      onChange={e => setForm({ ...form, patient: { ...form.patient, name: e.target.value }})}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"/>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t(lang, "common.age")}</label>
                    <input data-testid={BOOK.patientAge} type="number" value={form.patient.age}
                      onChange={e => setForm({ ...form, patient: { ...form.patient, age: +e.target.value }})}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"/>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t(lang, "common.gender")}</label>
                    <select data-testid={BOOK.patientGender} value={form.patient.gender}
                      onChange={e => setForm({ ...form, patient: { ...form.patient, gender: e.target.value }})}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
                      <option value="male">{t(lang, "common.male")}</option>
                      <option value="female">{t(lang, "common.female")}</option>
                      <option value="other">{t(lang, "common.other")}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t(lang, "common.phone")}</label>
                    <input data-testid={BOOK.patientPhone} value={form.patient.phone}
                      onChange={e => setForm({ ...form, patient: { ...form.patient, phone: e.target.value }})}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"/>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium">{t(lang, "common.email")}</label>
                    <input data-testid={BOOK.patientEmail} value={form.patient.email}
                      onChange={e => setForm({ ...form, patient: { ...form.patient, email: e.target.value }})}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"/>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium">{t(lang, "common.address")}</label>
                    <textarea rows={2} value={form.patient.address}
                      onChange={e => setForm({ ...form, patient: { ...form.patient, address: e.target.value }})}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"/>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              {step > 1 && <button onClick={() => setStep(step-1)} className="btn-outline">{t(lang, "common.back")}</button>}
              {step === 1 && (
                <button data-testid={BOOK.proceed} disabled={!canProceed()}
                  onClick={() => setStep(2)}
                  className={`btn-primary ml-auto ${!canProceed() ? "opacity-50 pointer-events-none" : ""}`}>
                  {t(lang, "common.next")}
                </button>
              )}
              {step === 2 && (
                <button data-testid={BOOK.pay} disabled={!canProceed() || placing}
                  onClick={() => placeBooking()}
                  className={`btn-primary ml-auto ${!canProceed() ? "opacity-50 pointer-events-none" : ""}`}>
                  {placing ? "…" : t(lang, "common.pay")} <IndianRupee size={16}/> {total}
                </button>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl card-shadow p-6 sticky top-24">
              <h3 className="font-heading font-semibold text-lg">{lang==="hi"?"सारांश":"Summary"}</h3>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Service</span><span className="font-medium">{serviceLabel}</span></div>
                {selectedTest && <div className="flex justify-between"><span className="text-slate-500">Test</span><span className="font-medium">{selectedTest.name}</span></div>}
                {selectedDoctor && <div className="flex justify-between"><span className="text-slate-500">Doctor</span><span className="font-medium">{selectedDoctor.name}</span></div>}
                {form.slot_date && <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="font-medium">{form.slot_date}</span></div>}
                {form.slot_time && <div className="flex justify-between"><span className="text-slate-500">Time</span><span className="font-medium">{form.slot_time}</span></div>}
              </div>
              <div className="border-t my-4"/>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">{t(lang, "common.platformFee")}</span><span>₹{platformFee}</span></div>
                {selectedTest && <div className="flex justify-between"><span className="text-slate-500">{t(lang, "common.testFee")}</span><span>₹{selectedTest.price}</span></div>}
                {service === "opd" && selectedDoctor && <div className="flex justify-between"><span className="text-slate-500">{t(lang, "common.consultFee")}</span><span>₹{selectedDoctor.fee}</span></div>}
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-emerald-600 font-medium">
                    <span>Coupon ({couponCode.toUpperCase()})</span><span>−₹{couponDiscount}</span>
                  </div>
                )}
              </div>

              {/* Coupon input */}
              <div className="mt-4 pt-4 border-t">
                {couponDiscount > 0 ? (
                  <button onClick={removeCoupon} className="text-sm text-red-500 hover:underline">Remove coupon</button>
                ) : (
                  <div className="flex gap-2">
                    <input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                      placeholder={lang==="hi"?"कूपन कोड":"Coupon code"}
                      data-testid="book-coupon-input"
                      className="flex-1 rounded-full bg-slate-50 border border-slate-200 px-4 py-2 text-sm outline-none focus:border-blue-500"/>
                    <button onClick={applyCoupon} data-testid="book-coupon-apply" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">Apply</button>
                  </div>
                )}
                {couponErr && <p className="text-xs text-red-500 mt-2">{couponErr}</p>}
                {publicCoupons.length > 0 && couponDiscount === 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {publicCoupons.filter(c => !c.applicable_services?.length || c.applicable_services.includes(service)).map(c => (
                      <button key={c.code} onClick={() => { setCouponCode(c.code); }} className="text-[10px] px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100">
                        {c.code} · {c.discount_percent ? `${c.discount_percent}% off` : `₹${c.discount_flat} off`}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t my-4"/>
              <div className="flex justify-between font-heading font-semibold text-lg">
                <span>{t(lang, "common.total")}</span><span>₹{total}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {payOpen && orderInfo && (
        <UpiQrPayModal
          open={payOpen}
          onClose={() => setPayOpen(false)}
          order={orderInfo}
          lang={lang}
          description={`${serviceLabel} · ${confirmed?.booking_id || ""}`}
          onPaid={completePayment}
        />
      )}

      <WhatsAppFab />
      <Footer />

      <OtpModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        phone={form.patient.phone}
        lang={lang}
        onVerified={(token, phone) => {
          setOtpToken(token);
          setForm(f => ({ ...f, patient: { ...f.patient, phone } }));
          setOtpOpen(false);
          placeBooking(token);
        }}
      />
    </div>
  );
}
